import { Accordion, Button, Switch } from '@risksmart-app/atomic-ui';
import type { ParentType as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { Edit02, Settings01 } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { BasicModule } from 'src/context/moduleContext';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

import SubModuleSettings from './SubmoduleSettings';
import { ThirdPartyPortalDiagnostics } from './ThirdPartyPortalDiagnostics';

const ModuleSettings: FC<{
  moduleId: string;
  enabled: boolean;
  toggleItem: (id: string) => void;
  onModuleToggle: (id: string) => void;
  setShowTabSettings: (show: boolean) => void;
  setTabSettingsParentType: (type: Parent_Type_Enum | undefined) => void;
  subModules?: Record<string, BasicModule>;
  allowTabConfig?: boolean;
  isDirty?: boolean;
}> = ({
  moduleId,
  enabled,
  toggleItem,
  onModuleToggle,
  setShowTabSettings,
  setTabSettingsParentType,
  subModules,
  allowTabConfig,
  isDirty,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'modules' });
  const navigate = useNavigate();
  const isCustomerSupport = useIsCustomerSupport();

  // Config actions stay visible but are disabled while there are unsaved changes
  // (Edit tabs needs a saved state — it resets tab config on save; Scoring
  // settings navigates away and would discard unsaved toggles).
  const showConfigSection =
    enabled && (!!allowTabConfig || moduleId === 'risk');
  // CS-only third-party portal provisioning diagnostic (RSP-6736). Making the
  // module expandable is gated on CS so non-CS admins never see an empty body.
  const showThirdPartyDiagnostics =
    isCustomerSupport && moduleId === 'third_party';
  // Sub-modules are revealed even when the module is off, so users can expand a
  // disabled module to discover the settings it offers before enabling it.
  const hasSubModules = !!subModules;
  const hasOptions =
    showConfigSection || hasSubModules || showThirdPartyDiagnostics;

  const titleBlock = (
    <span className={'flex flex-col text-left'}>
      <span className={'text-lg font-bold'}>
        {/* @ts-ignore */}
        {t(`titles.${moduleId}`)}
      </span>
      <span className={'mt-1 text-base font-normal text-grey500'}>
        {/* @ts-ignore */}
        {t(`descriptions.${moduleId}`)}
      </span>
    </span>
  );

  return (
    <Accordion.Item value={moduleId} variant={'card'}>
      <div className={'flex items-start'}>
        <div className={'shrink-0 pl-4 pt-4'}>
          <Switch
            checked={enabled}
            onCheckedChange={() => onModuleToggle(moduleId)}
            aria-label={`Toggle ${moduleId}`}
          />
        </div>
        {hasOptions ? (
          <Accordion.Header className={'min-w-0 flex-1'} variant={'card'}>
            <Accordion.Trigger className={'items-start'} variant={'card'}>
              {titleBlock}
            </Accordion.Trigger>
          </Accordion.Header>
        ) : (
          <div className={'min-w-0 flex-1 p-4'}>{titleBlock}</div>
        )}
      </div>
      {hasOptions && (
        <Accordion.Content className={'pt-0 text-base'} variant={'card'}>
          {showConfigSection && (
            <>
              <h4 className={'text-lg'}>{t('configuration')}</h4>
              <div className={'flex flex-col gap-4'}>
                {allowTabConfig && (
                  <Button
                    variant={'neutral'}
                    style={'outline'}
                    radius={'sm'}
                    className={'w-fit gap-2'}
                    disabled={isDirty}
                    title={isDirty ? t('unsaved_changes') : undefined}
                    onClick={() => {
                      setTabSettingsParentType(moduleId as Parent_Type_Enum);
                      setShowTabSettings(true);
                    }}
                  >
                    <Edit02 width={16} />
                    <span>{t('edit_tabs')}</span>
                  </Button>
                )}
                {moduleId === 'risk' && (
                  <Button
                    variant={'neutral'}
                    style={'outline'}
                    radius={'sm'}
                    className={'w-fit gap-2'}
                    disabled={isDirty}
                    title={isDirty ? t('unsaved_changes') : undefined}
                    onClick={() =>
                      navigate('/settings/modules/risk/scoring-settings')
                    }
                  >
                    <Settings01 width={16} />
                    <span>{t('scoring_settings')}</span>
                  </Button>
                )}
              </div>
            </>
          )}
          {subModules && (
            <SubModuleSettings
              subModules={subModules}
              toggleItem={toggleItem}
              moduleId={moduleId}
              setShowTabSettings={setShowTabSettings}
              setTabSettingsParentType={setTabSettingsParentType}
              isDirty={isDirty}
            />
          )}
          {showThirdPartyDiagnostics && <ThirdPartyPortalDiagnostics />}
        </Accordion.Content>
      )}
    </Accordion.Item>
  );
};

export default ModuleSettings;
